export type Sector =
  | 'food-hospitality'
  | 'energy'
  | 'industrials'
  | 'materials'
  | 'construction'
  | 'pharmaceutical';

export interface SectorInfo {
  id: Sector;
  label: string;
}

export interface InventoryNeed {
  id: string;
  itemName: string;
  quantity: number;
  unit: string;
  deliveryBy: string; // ISO date
}

export interface BusinessPartner {
  id: string;
  name: string;
  sector: Sector;
  initials: string;
  inventoryNeeds: InventoryNeed[];
}

export const allSectors: SectorInfo[] = [
  { id: 'food-hospitality', label: 'Food & Hospitality' },
  { id: 'energy', label: 'Energy' },
  { id: 'industrials', label: 'Industrials' },
  { id: 'materials', label: 'Materials' },
  { id: 'construction', label: 'Construction' },
  { id: 'pharmaceutical', label: 'Pharmaceutical' },
];

export const getSectorLabel = (sectorId: Sector): string => {
  const sector = allSectors.find((s) => s.id === sectorId);
  return sector?.label ?? sectorId;
};

export const allBusinessPartners: BusinessPartner[] = [
  {
    id: 'bp-1',
    name: 'Harvest Foods Co.',
    sector: 'food-hospitality',
    initials: 'HF',
    inventoryNeeds: [
      {
        id: 'n-1',
        itemName: 'Organic Wheat Flour',
        quantity: 5000,
        unit: 'kg',
        deliveryBy: '2026-04-15',
      },
      {
        id: 'n-2',
        itemName: 'Cold-Pressed Olive Oil',
        quantity: 1200,
        unit: 'liters',
        deliveryBy: '2026-04-10',
      },
      {
        id: 'n-3',
        itemName: 'Free-Range Eggs',
        quantity: 8000,
        unit: 'units',
        deliveryBy: '2026-04-08',
      },
      { id: 'n-4', itemName: 'Sea Salt', quantity: 300, unit: 'kg', deliveryBy: '2026-04-20' },
      {
        id: 'n-39',
        itemName: 'Organic Wheat Flour',
        quantity: 3000,
        unit: 'kg',
        deliveryBy: '2026-05-10',
      },
      {
        id: 'n-40',
        itemName: 'Organic Wheat Flour',
        quantity: 2000,
        unit: 'kg',
        deliveryBy: '2026-06-01',
      },
      {
        id: 'n-41',
        itemName: 'Free-Range Eggs',
        quantity: 5000,
        unit: 'units',
        deliveryBy: '2026-05-15',
      },
    ],
  },
  {
    id: 'bp-2',
    name: 'GreenGrid Energy',
    sector: 'energy',
    initials: 'GG',
    inventoryNeeds: [
      {
        id: 'n-5',
        itemName: 'Solar Panel Modules',
        quantity: 200,
        unit: 'units',
        deliveryBy: '2026-05-01',
      },
      {
        id: 'n-6',
        itemName: 'Lithium Battery Cells',
        quantity: 500,
        unit: 'units',
        deliveryBy: '2026-04-25',
      },
      {
        id: 'n-42',
        itemName: 'Solar Panel Modules',
        quantity: 150,
        unit: 'units',
        deliveryBy: '2026-06-15',
      },
    ],
  },
  {
    id: 'bp-3',
    name: 'Apex Industrial',
    sector: 'industrials',
    initials: 'AI',
    inventoryNeeds: [
      {
        id: 'n-7',
        itemName: 'Steel Beams (I-Type)',
        quantity: 150,
        unit: 'units',
        deliveryBy: '2026-04-18',
      },
      {
        id: 'n-8',
        itemName: 'Hydraulic Cylinders',
        quantity: 80,
        unit: 'units',
        deliveryBy: '2026-04-12',
      },
      {
        id: 'n-9',
        itemName: 'Industrial Bearings',
        quantity: 1000,
        unit: 'units',
        deliveryBy: '2026-04-22',
      },
      {
        id: 'n-10',
        itemName: 'Welding Wire Spools',
        quantity: 300,
        unit: 'units',
        deliveryBy: '2026-04-30',
      },
      {
        id: 'n-11',
        itemName: 'Safety Helmets',
        quantity: 500,
        unit: 'units',
        deliveryBy: '2026-05-05',
      },
    ],
  },
  {
    id: 'bp-4',
    name: 'CoreMat Supply',
    sector: 'materials',
    initials: 'CM',
    inventoryNeeds: [
      {
        id: 'n-12',
        itemName: 'Portland Cement',
        quantity: 20000,
        unit: 'kg',
        deliveryBy: '2026-04-14',
      },
      {
        id: 'n-13',
        itemName: 'Copper Wire (12 AWG)',
        quantity: 5000,
        unit: 'meters',
        deliveryBy: '2026-04-20',
      },
      {
        id: 'n-43',
        itemName: 'Portland Cement',
        quantity: 15000,
        unit: 'kg',
        deliveryBy: '2026-05-15',
      },
      {
        id: 'n-44',
        itemName: 'Portland Cement',
        quantity: 10000,
        unit: 'kg',
        deliveryBy: '2026-06-10',
      },
      {
        id: 'n-14',
        itemName: 'PVC Piping',
        quantity: 2000,
        unit: 'meters',
        deliveryBy: '2026-04-28',
      },
    ],
  },
  {
    id: 'bp-5',
    name: 'Pinnacle Realty',
    sector: 'construction',
    initials: 'PR',
    inventoryNeeds: [
      { id: 'n-15', itemName: 'HVAC Units', quantity: 25, unit: 'units', deliveryBy: '2026-05-10' },
      {
        id: 'n-16',
        itemName: 'Smart Lock Systems',
        quantity: 100,
        unit: 'units',
        deliveryBy: '2026-04-30',
      },
    ],
  },
  {
    id: 'bp-6',
    name: 'MedCore Pharma',
    sector: 'pharmaceutical',
    initials: 'MC',
    inventoryNeeds: [
      {
        id: 'n-17',
        itemName: 'API Compound (Ibuprofen)',
        quantity: 500,
        unit: 'kg',
        deliveryBy: '2026-04-10',
      },
      {
        id: 'n-18',
        itemName: 'Gelatin Capsules (Size 0)',
        quantity: 100000,
        unit: 'units',
        deliveryBy: '2026-04-12',
      },
      {
        id: 'n-19',
        itemName: 'Blister Packaging Foil',
        quantity: 3000,
        unit: 'meters',
        deliveryBy: '2026-04-08',
      },
      {
        id: 'n-20',
        itemName: 'Sterile Vials (10ml)',
        quantity: 20000,
        unit: 'units',
        deliveryBy: '2026-04-15',
      },
    ],
  },
  {
    id: 'bp-7',
    name: 'BrightStay Hotels',
    sector: 'food-hospitality',
    initials: 'BS',
    inventoryNeeds: [
      {
        id: 'n-21',
        itemName: 'Premium Bed Linens',
        quantity: 400,
        unit: 'sets',
        deliveryBy: '2026-04-20',
      },
      {
        id: 'n-22',
        itemName: 'Minibar Beverages',
        quantity: 5000,
        unit: 'units',
        deliveryBy: '2026-04-15',
      },
      {
        id: 'n-23',
        itemName: 'Bathroom Amenity Kits',
        quantity: 3000,
        unit: 'units',
        deliveryBy: '2026-04-12',
      },
    ],
  },
  {
    id: 'bp-8',
    name: 'VoltStream Power',
    sector: 'energy',
    initials: 'VS',
    inventoryNeeds: [
      {
        id: 'n-24',
        itemName: 'Transformer Coils',
        quantity: 50,
        unit: 'units',
        deliveryBy: '2026-04-18',
      },
      {
        id: 'n-25',
        itemName: 'High-Voltage Cables',
        quantity: 10000,
        unit: 'meters',
        deliveryBy: '2026-04-22',
      },
      {
        id: 'n-26',
        itemName: 'Circuit Breakers',
        quantity: 200,
        unit: 'units',
        deliveryBy: '2026-04-14',
      },
      {
        id: 'n-27',
        itemName: 'Insulation Material',
        quantity: 800,
        unit: 'kg',
        deliveryBy: '2026-04-25',
      },
    ],
  },
  {
    id: 'bp-9',
    name: 'SteelForge Works',
    sector: 'industrials',
    initials: 'SF',
    inventoryNeeds: [
      {
        id: 'n-28',
        itemName: 'Raw Iron Ore',
        quantity: 50000,
        unit: 'kg',
        deliveryBy: '2026-04-10',
      },
      {
        id: 'n-29',
        itemName: 'Carbon Electrodes',
        quantity: 100,
        unit: 'units',
        deliveryBy: '2026-04-16',
      },
    ],
  },
  {
    id: 'bp-10',
    name: 'UrbanNest Properties',
    sector: 'construction',
    initials: 'UN',
    inventoryNeeds: [
      {
        id: 'n-30',
        itemName: 'Hardwood Flooring',
        quantity: 5000,
        unit: 'sqft',
        deliveryBy: '2026-04-20',
      },
      {
        id: 'n-31',
        itemName: 'Energy-Efficient Windows',
        quantity: 150,
        unit: 'units',
        deliveryBy: '2026-05-01',
      },
      {
        id: 'n-32',
        itemName: 'Quartz Countertops',
        quantity: 75,
        unit: 'units',
        deliveryBy: '2026-04-28',
      },
      {
        id: 'n-33',
        itemName: 'LED Light Fixtures',
        quantity: 500,
        unit: 'units',
        deliveryBy: '2026-04-18',
      },
    ],
  },
  {
    id: 'bp-11',
    name: 'PureBlend Materials',
    sector: 'materials',
    initials: 'PB',
    inventoryNeeds: [
      {
        id: 'n-34',
        itemName: 'Silica Sand',
        quantity: 30000,
        unit: 'kg',
        deliveryBy: '2026-04-12',
      },
      {
        id: 'n-35',
        itemName: 'Epoxy Resin',
        quantity: 2000,
        unit: 'liters',
        deliveryBy: '2026-04-18',
      },
    ],
  },
  {
    id: 'bp-12',
    name: 'BioVita Labs',
    sector: 'pharmaceutical',
    initials: 'BV',
    inventoryNeeds: [
      {
        id: 'n-36',
        itemName: 'Microcrystalline Cellulose',
        quantity: 1000,
        unit: 'kg',
        deliveryBy: '2026-04-14',
      },
      {
        id: 'n-37',
        itemName: 'Stainless Steel Reactors',
        quantity: 5,
        unit: 'units',
        deliveryBy: '2026-05-15',
      },
      {
        id: 'n-38',
        itemName: 'HEPA Filter Cartridges',
        quantity: 50,
        unit: 'units',
        deliveryBy: '2026-04-20',
      },
    ],
  },
];

interface SupplierBid {
  id: string;
  supplierName: string;
  sellingPrice: number;
  currency: string;
  quantityOffered: number;
  collateralPosted: number;
  timestamp: string; // ISO datetime
}

export interface PartnerBidRow extends SupplierBid {
  needId: string;
  itemName: string;
  requiredQuantity: number;
  unit: string;
  status: 'active' | 'backup';
}

const allBids: Record<string, SupplierBid[]> = {
  // Harvest Foods Co.
  'n-1': [
    {
      id: 'b-1',
      supplierName: 'GrainWorks Ltd',
      sellingPrice: 2.5,
      currency: 'GHS',
      quantityOffered: 3000,
      collateralPosted: 7500,
      timestamp: '2026-03-20T08:00:00Z',
    },
    {
      id: 'b-2',
      supplierName: 'WheatField Co',
      sellingPrice: 2.8,
      currency: 'GHS',
      quantityOffered: 2500,
      collateralPosted: 7000,
      timestamp: '2026-03-20T10:30:00Z',
    },
    {
      id: 'b-3',
      supplierName: 'PureGrain Inc',
      sellingPrice: 2.6,
      currency: 'GHS',
      quantityOffered: 5000,
      collateralPosted: 13000,
      timestamp: '2026-03-21T09:00:00Z',
    },
  ],
  'n-2': [
    {
      id: 'b-4',
      supplierName: 'OliveGrove Trading',
      sellingPrice: 12.0,
      currency: 'GHS',
      quantityOffered: 800,
      collateralPosted: 9600,
      timestamp: '2026-03-19T14:00:00Z',
    },
    {
      id: 'b-5',
      supplierName: 'MedOil Exports',
      sellingPrice: 11.5,
      currency: 'GHS',
      quantityOffered: 600,
      collateralPosted: 6900,
      timestamp: '2026-03-20T07:00:00Z',
    },
  ],
  'n-3': [
    {
      id: 'b-6',
      supplierName: 'FarmFresh Poultry',
      sellingPrice: 0.45,
      currency: 'GHS',
      quantityOffered: 5000,
      collateralPosted: 2250,
      timestamp: '2026-03-18T11:00:00Z',
    },
    {
      id: 'b-7',
      supplierName: 'EggCo Farms',
      sellingPrice: 0.5,
      currency: 'GHS',
      quantityOffered: 4000,
      collateralPosted: 2000,
      timestamp: '2026-03-19T09:00:00Z',
    },
    {
      id: 'b-8',
      supplierName: 'Golden Yolk Ltd',
      sellingPrice: 0.42,
      currency: 'GHS',
      quantityOffered: 8000,
      collateralPosted: 3360,
      timestamp: '2026-03-20T16:00:00Z',
    },
  ],
  'n-4': [
    {
      id: 'b-9',
      supplierName: 'SeaMineral Co',
      sellingPrice: 3.0,
      currency: 'GHS',
      quantityOffered: 300,
      collateralPosted: 900,
      timestamp: '2026-03-21T10:00:00Z',
    },
  ],
  // GreenGrid Energy
  'n-5': [
    {
      id: 'b-10',
      supplierName: 'SolarTech Mfg',
      sellingPrice: 450.0,
      currency: 'GHS',
      quantityOffered: 120,
      collateralPosted: 54000,
      timestamp: '2026-03-22T08:00:00Z',
    },
    {
      id: 'b-11',
      supplierName: 'BrightPanel Corp',
      sellingPrice: 430.0,
      currency: 'GHS',
      quantityOffered: 100,
      collateralPosted: 43000,
      timestamp: '2026-03-22T12:00:00Z',
    },
    {
      id: 'b-12',
      supplierName: 'GreenModule Ltd',
      sellingPrice: 460.0,
      currency: 'GHS',
      quantityOffered: 200,
      collateralPosted: 92000,
      timestamp: '2026-03-23T09:00:00Z',
    },
  ],
  'n-6': [
    {
      id: 'b-13',
      supplierName: 'PowerCell Inc',
      sellingPrice: 85.0,
      currency: 'GHS',
      quantityOffered: 300,
      collateralPosted: 25500,
      timestamp: '2026-03-21T15:00:00Z',
    },
    {
      id: 'b-14',
      supplierName: 'LithTech Supply',
      sellingPrice: 82.0,
      currency: 'GHS',
      quantityOffered: 250,
      collateralPosted: 20500,
      timestamp: '2026-03-22T09:00:00Z',
    },
  ],
  // Apex Industrial
  'n-7': [
    {
      id: 'b-15',
      supplierName: 'MetalForge Corp',
      sellingPrice: 320.0,
      currency: 'GHS',
      quantityOffered: 100,
      collateralPosted: 32000,
      timestamp: '2026-03-20T07:00:00Z',
    },
    {
      id: 'b-16',
      supplierName: 'SteelMaster Ltd',
      sellingPrice: 310.0,
      currency: 'GHS',
      quantityOffered: 80,
      collateralPosted: 24800,
      timestamp: '2026-03-21T11:00:00Z',
    },
  ],
  'n-8': [
    {
      id: 'b-17',
      supplierName: 'HydroSys Inc',
      sellingPrice: 750.0,
      currency: 'GHS',
      quantityOffered: 50,
      collateralPosted: 37500,
      timestamp: '2026-03-19T10:00:00Z',
    },
    {
      id: 'b-18',
      supplierName: 'PressureTech',
      sellingPrice: 720.0,
      currency: 'GHS',
      quantityOffered: 40,
      collateralPosted: 28800,
      timestamp: '2026-03-20T14:00:00Z',
    },
  ],
  'n-9': [
    {
      id: 'b-19',
      supplierName: 'BearingWorld',
      sellingPrice: 8.5,
      currency: 'GHS',
      quantityOffered: 600,
      collateralPosted: 5100,
      timestamp: '2026-03-21T08:00:00Z',
    },
    {
      id: 'b-20',
      supplierName: 'PrecisionParts Co',
      sellingPrice: 9.0,
      currency: 'GHS',
      quantityOffered: 500,
      collateralPosted: 4500,
      timestamp: '2026-03-22T10:00:00Z',
    },
  ],
  'n-10': [
    {
      id: 'b-21',
      supplierName: 'WeldSupply Ltd',
      sellingPrice: 15.0,
      currency: 'GHS',
      quantityOffered: 300,
      collateralPosted: 4500,
      timestamp: '2026-03-23T09:00:00Z',
    },
  ],
  'n-11': [
    {
      id: 'b-22',
      supplierName: 'SafetyGear Pro',
      sellingPrice: 12.0,
      currency: 'GHS',
      quantityOffered: 500,
      collateralPosted: 6000,
      timestamp: '2026-03-22T07:00:00Z',
    },
  ],
  // CoreMat Supply
  'n-12': [
    {
      id: 'b-23',
      supplierName: 'CementWorks Ghana',
      sellingPrice: 0.35,
      currency: 'GHS',
      quantityOffered: 15000,
      collateralPosted: 5250,
      timestamp: '2026-03-18T08:00:00Z',
    },
    {
      id: 'b-24',
      supplierName: 'BuildMat Corp',
      sellingPrice: 0.38,
      currency: 'GHS',
      quantityOffered: 10000,
      collateralPosted: 3800,
      timestamp: '2026-03-19T12:00:00Z',
    },
    {
      id: 'b-25',
      supplierName: 'Quarry Direct Ltd',
      sellingPrice: 0.33,
      currency: 'GHS',
      quantityOffered: 20000,
      collateralPosted: 6600,
      timestamp: '2026-03-20T10:00:00Z',
    },
  ],
  'n-13': [
    {
      id: 'b-26',
      supplierName: 'CopperLine Trading',
      sellingPrice: 4.2,
      currency: 'GHS',
      quantityOffered: 3000,
      collateralPosted: 12600,
      timestamp: '2026-03-20T09:00:00Z',
    },
    {
      id: 'b-27',
      supplierName: 'WireMax Ltd',
      sellingPrice: 4.0,
      currency: 'GHS',
      quantityOffered: 5000,
      collateralPosted: 20000,
      timestamp: '2026-03-21T14:00:00Z',
    },
  ],
  'n-14': [
    {
      id: 'b-28',
      supplierName: 'PipeTech Solutions',
      sellingPrice: 6.5,
      currency: 'GHS',
      quantityOffered: 2000,
      collateralPosted: 13000,
      timestamp: '2026-03-22T11:00:00Z',
    },
  ],
  // Pinnacle Realty
  'n-15': [
    {
      id: 'b-29',
      supplierName: 'ClimateControl Co',
      sellingPrice: 3500.0,
      currency: 'GHS',
      quantityOffered: 15,
      collateralPosted: 52500,
      timestamp: '2026-03-21T08:00:00Z',
    },
    {
      id: 'b-30',
      supplierName: 'CoolAir Systems',
      sellingPrice: 3200.0,
      currency: 'GHS',
      quantityOffered: 12,
      collateralPosted: 38400,
      timestamp: '2026-03-22T10:00:00Z',
    },
  ],
  'n-16': [
    {
      id: 'b-31',
      supplierName: 'SecureLock Tech',
      sellingPrice: 180.0,
      currency: 'GHS',
      quantityOffered: 60,
      collateralPosted: 10800,
      timestamp: '2026-03-20T15:00:00Z',
    },
    {
      id: 'b-32',
      supplierName: 'SmartEntry Ltd',
      sellingPrice: 195.0,
      currency: 'GHS',
      quantityOffered: 50,
      collateralPosted: 9750,
      timestamp: '2026-03-21T11:00:00Z',
    },
  ],
  // MedCore Pharma
  'n-17': [
    {
      id: 'b-33',
      supplierName: 'ChemPure Labs',
      sellingPrice: 45.0,
      currency: 'GHS',
      quantityOffered: 300,
      collateralPosted: 13500,
      timestamp: '2026-03-19T08:00:00Z',
    },
    {
      id: 'b-34',
      supplierName: 'PharmaChem Inc',
      sellingPrice: 48.0,
      currency: 'GHS',
      quantityOffered: 250,
      collateralPosted: 12000,
      timestamp: '2026-03-20T10:00:00Z',
    },
  ],
  'n-18': [
    {
      id: 'b-35',
      supplierName: 'CapsuleTech Mfg',
      sellingPrice: 0.02,
      currency: 'GHS',
      quantityOffered: 60000,
      collateralPosted: 1200,
      timestamp: '2026-03-20T07:00:00Z',
    },
    {
      id: 'b-36',
      supplierName: 'GelatinWorks',
      sellingPrice: 0.018,
      currency: 'GHS',
      quantityOffered: 50000,
      collateralPosted: 900,
      timestamp: '2026-03-21T09:00:00Z',
    },
    {
      id: 'b-37',
      supplierName: 'PharmaShell Ltd',
      sellingPrice: 0.022,
      currency: 'GHS',
      quantityOffered: 100000,
      collateralPosted: 2200,
      timestamp: '2026-03-22T13:00:00Z',
    },
  ],
  'n-19': [
    {
      id: 'b-38',
      supplierName: 'PackFoil Trading',
      sellingPrice: 1.8,
      currency: 'GHS',
      quantityOffered: 2000,
      collateralPosted: 3600,
      timestamp: '2026-03-18T14:00:00Z',
    },
    {
      id: 'b-39',
      supplierName: 'BlisterPack Co',
      sellingPrice: 1.95,
      currency: 'GHS',
      quantityOffered: 1500,
      collateralPosted: 2925,
      timestamp: '2026-03-19T11:00:00Z',
    },
  ],
  'n-20': [
    {
      id: 'b-40',
      supplierName: 'VialCraft Inc',
      sellingPrice: 0.75,
      currency: 'GHS',
      quantityOffered: 12000,
      collateralPosted: 9000,
      timestamp: '2026-03-20T09:00:00Z',
    },
    {
      id: 'b-41',
      supplierName: 'SterileGlass Ltd',
      sellingPrice: 0.8,
      currency: 'GHS',
      quantityOffered: 10000,
      collateralPosted: 8000,
      timestamp: '2026-03-21T15:00:00Z',
    },
  ],
};

export const getBidsForNeed = (needId: string): SupplierBid[] => {
  return allBids[needId] ?? [];
};

export const categorizeBids = (
  bids: SupplierBid[],
  requiredQuantity: number,
): { primaryBids: SupplierBid[]; backupBids: SupplierBid[] } => {
  const sorted = [...bids].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const primaryBids: SupplierBid[] = [];
  const backupBids: SupplierBid[] = [];
  let accumulated = 0;

  for (const bid of sorted) {
    if (accumulated < requiredQuantity) {
      primaryBids.push(bid);
      accumulated += bid.quantityOffered;
    } else {
      backupBids.push(bid);
    }
  }

  return { primaryBids, backupBids };
};

export const getAllBidsForPartner = (partner: BusinessPartner): PartnerBidRow[] => {
  const rows: PartnerBidRow[] = [];

  for (const need of partner.inventoryNeeds) {
    const bids = getBidsForNeed(need.id);
    const { primaryBids, backupBids } = categorizeBids(bids, need.quantity);

    for (const bid of primaryBids) {
      rows.push({
        ...bid,
        needId: need.id,
        itemName: need.itemName,
        requiredQuantity: need.quantity,
        unit: need.unit,
        status: 'active',
      });
    }
    for (const bid of backupBids) {
      rows.push({
        ...bid,
        needId: need.id,
        itemName: need.itemName,
        requiredQuantity: need.quantity,
        unit: need.unit,
        status: 'backup',
      });
    }
  }

  return rows;
};

export const getEarliestDeliveryDate = (needs: InventoryNeed[]): string | null => {
  if (needs.length === 0) {
    return null;
  }
  return needs.reduce((earliest, need) => {
    return need.deliveryBy < earliest ? need.deliveryBy : earliest;
  }, needs[0].deliveryBy);
};

export const getPartnersBySector = (sectorId: Sector): BusinessPartner[] => {
  return allBusinessPartners.filter((bp) => bp.sector === sectorId);
};

export interface AggregatedDemandItem {
  itemName: string;
  unit: string;
  totalQuantity: number;
  coveredQuantity: number;
  coveragePercent: number;
  earliestDelivery: string;
  partners: {
    partnerId: string;
    partnerName: string;
    partnerInitials: string;
    needId: string;
    quantity: number;
    coveredQuantity: number;
    coveragePercent: number;
  }[];
}

export const getAggregatedDemand = (partners: BusinessPartner[]): AggregatedDemandItem[] => {
  const itemMap = new Map<string, AggregatedDemandItem>();

  for (const partner of partners) {
    for (const need of partner.inventoryNeeds) {
      const key = `${need.itemName}__${need.unit}`;
      const bids = getBidsForNeed(need.id);
      const { primaryBids } = categorizeBids(bids, need.quantity);
      const covered = Math.min(
        primaryBids.reduce((sum, bid) => sum + bid.quantityOffered, 0),
        need.quantity,
      );

      const partnerEntry = {
        partnerId: partner.id,
        partnerName: partner.name,
        partnerInitials: partner.initials,
        needId: need.id,
        quantity: need.quantity,
        coveredQuantity: covered,
        coveragePercent: need.quantity > 0 ? Math.round((covered / need.quantity) * 100) : 0,
      };

      const existing = itemMap.get(key);
      if (existing != null) {
        existing.totalQuantity += need.quantity;
        existing.coveredQuantity += covered;
        existing.partners.push(partnerEntry);
        if (need.deliveryBy < existing.earliestDelivery) {
          existing.earliestDelivery = need.deliveryBy;
        }
        existing.coveragePercent =
          existing.totalQuantity > 0
            ? Math.round((existing.coveredQuantity / existing.totalQuantity) * 100)
            : 0;
      } else {
        itemMap.set(key, {
          itemName: need.itemName,
          unit: need.unit,
          totalQuantity: need.quantity,
          coveredQuantity: covered,
          coveragePercent: need.quantity > 0 ? Math.round((covered / need.quantity) * 100) : 0,
          earliestDelivery: need.deliveryBy,
          partners: [partnerEntry],
        });
      }
    }
  }

  return Array.from(itemMap.values());
};
